/**
 * Pure (Phaser-free) camera-zoom math, shared by wheel zoom, pinch zoom, and the
 * on-screen +/- buttons so they all agree on a single min/max and step size.
 *
 * `fitZoom` is the camera zoom at which the 1280x720 design map exactly fits the
 * current viewport:
 *  - In Phaser.Scale.FIT mode the scale manager does the fitting, so the camera
 *    sits at zoom 1 and `fitZoom` is 1 (the default).
 *  - In Phaser.Scale.RESIZE mode the camera itself must zoom to fit, so callers
 *    pass `fitZoom = min(viewW/designW, viewH/designH)` and the usable range
 *    becomes `[fitZoom, fitZoom * MAX_ZOOM_FACTOR]`.
 */

/** Minimum zoom relative to "fit". Below fit only adds letterboxing, so clamp to fit. */
export const MIN_ZOOM_FACTOR = 1.0;
/** Maximum zoom relative to "fit". Matches the previous desktop wheel maximum. */
export const MAX_ZOOM_FACTOR = 2.65;
/** Multiplicative step when zooming in one notch / pressing "+". */
export const WHEEL_STEP_IN = 1.14;
/** Multiplicative step when zooming out one notch / pressing "-". */
export const WHEEL_STEP_OUT = 0.88;

/**
 * Clamp a desired camera zoom into the allowed range for the current fit.
 * @param zoom desired camera zoom
 * @param fitZoom camera zoom at which the design map fits the viewport (1 in FIT mode)
 */
export function clampZoom(zoom: number, fitZoom = 1): number {
  const min = MIN_ZOOM_FACTOR * fitZoom;
  const max = MAX_ZOOM_FACTOR * fitZoom;
  return Math.min(max, Math.max(min, zoom));
}

/**
 * Clamp one axis of a Phaser camera's scroll so the visible world rect stays inside the
 * map, accounting for the camera's 0.5 origin.
 *
 * With origin 0.5, `scroll` is NOT the visible top-left: the visible near edge sits at
 * `scroll + (cameraSize - viewSize) / 2`, where `viewSize = cameraSize / zoom`. Clamping
 * `scroll` naively to `[0, mapSize - viewSize]` (origin-0 assumption) lets the view spill
 * past the far edge and reveal the black void — the bug this corrects.
 *
 * @param scroll current camera scrollX/scrollY
 * @param cameraSize camera.width / camera.height (design px)
 * @param zoom current camera zoom
 * @param mapSize map extent on this axis (designWidth / designHeight)
 * @returns the clamped scroll value (centred when the view is larger than the map)
 */
export function clampCameraScrollAxis(scroll: number, cameraSize: number, zoom: number, mapSize: number): number {
  const viewSize = cameraSize / zoom;
  const offset = (cameraSize - viewSize) / 2;
  if (viewSize >= mapSize) {
    // View covers the whole map on this axis → centre it.
    return (mapSize - viewSize) / 2 - offset;
  }
  return Math.min(Math.max(scroll, -offset), mapSize - viewSize - offset);
}
